import { memo } from 'react'
import { 
  TruckIcon, 
  UserIcon, 
  MapPinIcon, 
  ChatBubbleLeftIcon,
  CalendarIcon 
} from '@heroicons/react/24/outline'

/**
 * SightingDetailsPanel - Display sighting details (entity, reporter, location, notes)
 * 
 * @param {Object} sighting - Sighting data
 * @param {Object} entity - Associated entity data
 * 
 * @idempotent - Pure presentation component with React.memo
 * @atomic - Single responsibility: details display (<150 lines)
 */
const SightingDetailsPanel = memo(function SightingDetailsPanel({ sighting, entity }) {
  if (!sighting) return null

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Sighting Information</h3>
      
      <div className="space-y-4">
        {/* Entity */}
        <div className="flex items-start">
          <TruckIcon className="h-5 w-5 mr-3 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-gray-500">Entity</div>
            <div className="text-gray-900 font-medium">
              {sighting.entityName || entity?.name || 'Unknown Entity'}
            </div>
            {(sighting.type || entity?.entityType) && (
              <div className="text-sm text-gray-500 capitalize">
                {sighting.type || entity.type}
              </div>
            )}
          </div>
        </div>

        {/* Reporter */}
        <div className="flex items-start">
          <UserIcon className="h-5 w-5 mr-3 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-gray-500">Reported By</div>
            <div className="text-gray-900">
              {sighting.userName || sighting.reporterName || 'Anonymous'}
            </div>
            {sighting.userEmail && (
              <div className="text-sm text-gray-500">{sighting.userEmail}</div>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="flex items-start">
          <MapPinIcon className="h-5 w-5 mr-3 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-gray-500">Location</div>
            <div className="text-gray-900">
              {sighting.address || 'Address not available'}
            </div>
            {sighting.latitude && sighting.longitude && (
              <>
                <div className="text-sm text-gray-500 mt-1 font-mono">
                  {sighting.latitude.toFixed(6)}, {sighting.longitude.toFixed(6)}
                </div>
                <a
                  href={`https://www.google.com/maps?q=${sighting.latitude},${sighting.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:text-primary-600 mt-1 inline-block"
                >
                  View on Google Maps →
                </a>
              </>
            )}
          </div>
        </div>

        {/* Notes */}
        {sighting.notes && (
          <div className="flex items-start">
            <ChatBubbleLeftIcon className="h-5 w-5 mr-3 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm text-gray-500">Notes</div>
              <div className="text-gray-900 whitespace-pre-wrap">{sighting.notes}</div>
            </div>
          </div>
        )}

        {/* Timestamp */}
        <div className="flex items-start">
          <CalendarIcon className="h-5 w-5 mr-3 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-gray-500">Reported</div>
            <div className="text-gray-900">
              {new Date(sighting.createdAt).toLocaleString()}
            </div>
            {sighting.sightedAt && sighting.sightedAt !== sighting.createdAt && (
              <div className="text-sm text-gray-500 mt-1">
                Sighted: {new Date(sighting.sightedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Count */}
        {sighting.confirmationCount !== undefined && (
          <div className="pt-3 border-t">
            <div className="text-sm text-gray-500">Confirmations</div>
            <div className="text-2xl font-bold text-primary">
              {sighting.confirmationCount}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {sighting.confirmationCount === 1 ? 'user has' : 'users have'} confirmed this sighting
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default SightingDetailsPanel
